import { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';

import { useAction } from '../../../Common/hooks';
import { setFlyoutTitle } from '../../../Dashboard/actions';
import {
  setCredentialFormCreateMode,
  setCredentialFormIsLoading,
  setCredentialFormIsNotLoading,
  setHideCredentialForm,
  fetchSaveCredential,
} from '../../actions';
import {
  selectCredentialFormIsLoading,
  selectCredentialFormMode,
  selectCurrentSelectedAgency,
  selectCurrentSelectedCredential,
} from '../../selectors';
import { getNodeOptions } from '../../services';
import { text } from '../../constants';
import { shouldDisableSave } from '../../utils';

const useCredentialForm = () => {
  const [values, setValues] = useState({
    payerUsername: '',
    payerPassword: '',
    aggregatorID: '',
    businessMedicaidID: '',
    businessName: '',
    businessEntityID: '',
    businessNPI: '',
    businessTaxonomy: '',
    businessEIN: '',
    claimLayoutName: '',
    email: '',
    vendorID: '',
    includeBilling: true,
    splitVisit: false,
  });
  const [nodePickerOptions, setNodePickerOptions] = useState([]);
  const [isSaveDisabled, setIsSaveDisabled] = useState(true);
  const [spinnerMessage, setSpinnerMessage] = useState(text.loading);
  const dispatchHideCredentialForm = useAction(setHideCredentialForm);
  const dispatchSetCredentialFormCreateMode = useAction(setCredentialFormCreateMode);
  const dispatchSetCredentialFormIsLoading = useAction(setCredentialFormIsLoading);
  const dispatchSetCredentialFormIsNotLoading = useAction(setCredentialFormIsNotLoading);
  const dispatchSetFlyoutTitle = useAction(setFlyoutTitle);
  const dispatchFetchSaveCredential = useAction(fetchSaveCredential);
  const currentSelectedCredential = useSelector(selectCurrentSelectedCredential);
  const { agencyName } = useSelector(selectCurrentSelectedAgency);
  const isFormLoading = useSelector(selectCredentialFormIsLoading);
  const formMode = useSelector(selectCredentialFormMode);

  useEffect(() => {
    if (formMode === text.updateMode) {
      dispatchSetFlyoutTitle(`Edit Credential - ${currentSelectedCredential.payerUsername}`);
    } else {
      dispatchSetFlyoutTitle(text.createCredentialFormTitle);
    }

    return () => {
      dispatchSetFlyoutTitle(agencyName);
    };
  }, []);

  useEffect(() => {
    const callNodeOptions = async () => {
      dispatchSetCredentialFormIsLoading();
      const nodeOptions = await getNodeOptions();

      setNodePickerOptions(nodeOptions);
      dispatchSetCredentialFormIsNotLoading();
    };

    callNodeOptions();
  }, []);

  useEffect(() => {
    if (formMode === text.updateMode) {
      setValues({
        ...values,
        ...currentSelectedCredential,
      });
    }
  }, [currentSelectedCredential]);

  useEffect(() => {
    if (shouldDisableSave(values, currentSelectedCredential)) {
      setIsSaveDisabled(true);

      return;
    }

    setIsSaveDisabled(false);
  }, [values]);

  const handleToggleChange = field => () => {
    setValues({
      ...values,
      [field]: !values[field],
    });
  };

  const handleValueChange = field => event => {
    setValues({
      ...values,
      [field]: event.target.value,
    });
  };

  const handleCancel = () => {
    dispatchHideCredentialForm();
    dispatchSetCredentialFormCreateMode();
  };

  const handleSave = () => {
    setSpinnerMessage(text.saving);
    dispatchFetchSaveCredential(values);
  };

  return {
    values,
    handleToggleChange,
    handleValueChange,
    handleCancel,
    handleSave,
    isFormLoading,
    nodePickerOptions,
    isSaveDisabled,
    spinnerMessage,
  };
};

export default useCredentialForm;
